import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/modules/auth/stores/authStore';
import { api } from '@/services/api';

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [avatarUrl] = useState(user?.avatarUrl || '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('이미지 크기는 5MB 이하만 가능합니다');
      return;
    }

    // 이미지 타입 체크
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('지원하는 이미지 형식: JPEG, PNG, GIF, WebP');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!selectedFile) return avatarUrl || null;

    setIsUploading(true);
    try {
      // 1. Presigned URL 요청
      const presignResponse = await api.post<{
        presignedUrl: string;
        key: string;
      }>('/users/me/avatar/presign', {
        contentType: selectedFile.type,
        fileExtension: selectedFile.name.split('.').pop(),
      });

      // 2. S3에 직접 업로드
      await fetch(presignResponse.presignedUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });

      // 3. 업로드 완료 알림 및 DB 저장
      const saveResponse = await api.put<{
        avatarUrl: string;
      }>('/users/me/avatar', {
        key: presignResponse.key,
      });

      return saveResponse.avatarUrl;
    } catch (err) {
      console.error('Avatar upload failed:', err);
      throw new Error('프로필 사진 업로드에 실패했습니다');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!nickname.trim()) {
      setError('닉네임을 입력해주세요');
      return;
    }

    if (nickname.length < 2 || nickname.length > 20) {
      setError('닉네임은 2~20자 사이로 입력해주세요');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // 아바타 업로드
      const newAvatarUrl = await uploadAvatar();

      // 로컬 상태 업데이트
      if (user) {
        setUser({
          ...user,
          nickname: nickname.trim(),
          avatarUrl: newAvatarUrl || user.avatarUrl,
        });
      }
      navigate(-1);
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  const displayAvatar = avatarPreview || avatarUrl;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-b border-slate-700">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-400 hover:text-white"
        >
          ← 취소
        </button>
        <h1 className="text-lg font-semibold">프로필 편집</h1>
        <button
          onClick={handleSave}
          disabled={isSaving || isUploading}
          className="text-primary-400 hover:text-primary-300 font-semibold disabled:opacity-50"
        >
          {isSaving || isUploading ? '저장 중...' : '저장'}
        </button>
      </header>

      <div className="p-4 max-w-lg mx-auto">
        {/* 프로필 이미지 */}
        <div className="flex flex-col items-center mb-8">
          <div 
            className="relative w-24 h-24 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center mb-4 overflow-hidden cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            {displayAvatar ? (
              <img 
                src={displayAvatar} 
                alt="프로필" 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-4xl">😎</span>
            )}
            
            {/* 호버 오버레이 */}
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <span className="text-white text-xs">변경</span>
            </div>

            {/* 업로드 중 표시 */}
            {isUploading && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />

          <button 
            onClick={() => fileInputRef.current?.click()}
            className="text-primary-400 text-sm hover:text-primary-300"
          >
            프로필 사진 변경
          </button>
          <p className="text-slate-500 text-xs mt-1">
            JPEG, PNG, GIF, WebP (최대 5MB)
          </p>
        </div>

        {/* 폼 */}
        <div className="space-y-6">
          {/* 닉네임 */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-2">
              닉네임
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="닉네임을 입력하세요"
              maxLength={20}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg 
                       text-white placeholder-slate-500
                       focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <p className="text-slate-500 text-xs mt-1">
              {nickname.length}/20자
            </p>
          </div>

          {/* 이메일 (읽기 전용) */}
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-2">
              이메일
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg 
                       text-slate-400 cursor-not-allowed"
            />
            <p className="text-slate-500 text-xs mt-1">
              이메일은 변경할 수 없습니다
            </p>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
